import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";

/**
 * Phase 26 — "¿Olvidaste tu contraseña?"
 * Always shows the same Spanish success message whether the email exists or not
 * (prevents account enumeration). Backend enforces a 3/hr per-IP rate limit
 * (returns 429), which we surface with a distinct Spanish toast.
 */
export default function OlvideContrasenaPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const GENERIC =
    "Si hay una cuenta con ese correo, recibirás un enlace en breve. Revisa también tu carpeta de spam.";

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      toast.success("Enlace enviado si el correo existe");
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429) {
        toast.error("Demasiadas solicitudes. Inténtalo más tarde.");
      } else if (code === 422) {
        toast.error("Introduce un email válido.");
      } else {
        // Even on other errors we still show the generic message to avoid
        // leaking information — but log for the operator.
        setSent(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="olvide-page"
      className="mx-auto max-w-md px-6 py-16 sm:py-24"
    >
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-[#00953B] text-white grid place-items-center font-black">
            M
          </div>
          <span className="text-2xl font-black tracking-tight">
            Merca<span className="text-[#00953B]">Go</span>
          </span>
        </Link>
      </div>
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8">
        <button
          type="button"
          onClick={() => navigate("/entrar")}
          data-testid="olvide-back-btn"
          className="text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> Volver al inicio de sesión
        </button>
        <h1 className="text-2xl font-black tracking-tight mt-3">¿Olvidaste tu contraseña?</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Introduce tu correo y te enviaremos un enlace para elegir una contraseña nueva.
        </p>

        {sent ? (
          <div
            data-testid="olvide-success-msg"
            className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 text-sm leading-relaxed"
          >
            <div className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5" />
              <span>{GENERIC}</span>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-6 space-y-4"
            data-testid="olvide-form"
          >
            <div>
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="olvide-email-input"
                className="mt-1 h-11"
                placeholder="tu@email.com"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || !email.trim()}
              data-testid="olvide-submit-btn"
              className="w-full h-11 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white font-semibold"
            >
              {submitting ? "Enviando…" : "Enviar enlace"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
